import React, { Component, ErrorInfo, ReactNode } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  alpha,
} from '@mui/material';
import { ErrorOutline, Refresh, Logout } from '@mui/icons-material';

interface Props {
  children: ReactNode;
  onLogout?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary component that catches errors in subscription-related components.
 * Provides a fallback UI with options to retry or logout.
 */
class SubscriptionErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Subscription Error Boundary caught an error:', error, errorInfo);
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  private handleLogout = () => {
    if (this.props.onLogout) {
      this.props.onLogout();
    } else {
      // Fallback: clear storage and redirect
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
  };

  public render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            bgcolor: 'background.default',
            p: 3,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 4,
              maxWidth: 500,
              textAlign: 'center',
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 3,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: (theme) => alpha(theme.palette.error.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <ErrorOutline sx={{ fontSize: 40, color: 'error.main' }} />
            </Box>

            <Typography variant="h5" fontWeight={700} gutterBottom>
              Something went wrong
            </Typography>

            <Typography color="text.secondary" sx={{ mb: 3 }}>
              We encountered an error while loading your subscription information. 
              This might be a temporary issue.
            </Typography>

            {this.state.error && (
              <Paper
                sx={{
                  p: 2,
                  mb: 3,
                  bgcolor: (theme) => alpha(theme.palette.error.main, 0.05),
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                >
                  {this.state.error.message}
                </Typography>
              </Paper>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={this.handleRetry}
                sx={{ borderRadius: 2 }}
              >
                Try Again
              </Button>
              <Button
                variant="outlined"
                startIcon={<Logout />}
                onClick={this.handleLogout}
                sx={{ borderRadius: 2 }}
              >
                Logout
              </Button>
            </Box>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default SubscriptionErrorBoundary;
